import { TRANSLATION_KEYS } from 'locales/translation-keys'
import type { Cell, TableData } from 'utils/data'
import { TIME_DIMENSIONS } from './constants'
import { isCompany, isStore } from 'utils/location'
import { adaptStatusColorToClass } from 'utils/style-adapters'
import { TFunction } from 'i18next'

export const convertTimeDimensionToGFFormat = (dimension: string): string => {
    const dimensionMap: Record<string, string> = {
        [TIME_DIMENSIONS.PRIOR_DAY]: 'yesterday',
        [TIME_DIMENSIONS.WEEK_TO_DAY]: 'week_to_day',
        [TIME_DIMENSIONS.PRIOR_WEEK]: 'last_week',
        [TIME_DIMENSIONS.LAST_4_WEEKS]: 'last_4_weeks',
        [TIME_DIMENSIONS.MONTH_TO_DAY]: 'month_to_day',
        [TIME_DIMENSIONS.MONTH_TO_WEEK]: 'month_to_week',
        [TIME_DIMENSIONS.PRIOR_MONTH]: 'prior_month',
        [TIME_DIMENSIONS.QUARTER_TO_WEEK]: 'quarter_to_week',
        [TIME_DIMENSIONS.PRIOR_QUARTER]: 'prior_quarter',
        [TIME_DIMENSIONS.YEAR_TO_DAY]: 'year_to_day',
        [TIME_DIMENSIONS.YEAR_TO_WEEK]: 'year_to_week',
        [TIME_DIMENSIONS.PRIOR_YEAR]: 'last_year',
    }
    return dimensionMap[dimension] || dimension
}

/**
 * Map time dimension to translation key (short version for historical details)
 */
export const getTimeDimensionTranslationKey = (dimension: string): string => {
    const { timePeriods } = TRANSLATION_KEYS.myTargetPerformance
    const keyMap: Record<string, string> = {
        [TIME_DIMENSIONS.PRIOR_DAY]: timePeriods.priorDay,
        [TIME_DIMENSIONS.PRIOR_WEEK]: timePeriods.priorWeek,
        [TIME_DIMENSIONS.LAST_4_WEEKS]: timePeriods.last4Weeks,
        [TIME_DIMENSIONS.PRIOR_MONTH]: timePeriods.priorMonth,
        [TIME_DIMENSIONS.PRIOR_QUARTER]: timePeriods.priorQuarter,
        [TIME_DIMENSIONS.PRIOR_YEAR]: timePeriods.priorYear,
        [TIME_DIMENSIONS.WEEK_TO_DAY]: timePeriods.weekToDay,
        [TIME_DIMENSIONS.MONTH_TO_DAY]: timePeriods.monthToDay,
        [TIME_DIMENSIONS.MONTH_TO_WEEK]: timePeriods.monthToWeek,
        [TIME_DIMENSIONS.QUARTER_TO_WEEK]: timePeriods.quarterToWeek,
        [TIME_DIMENSIONS.YEAR_TO_DAY]: timePeriods.yearToDay,
        [TIME_DIMENSIONS.YEAR_TO_WEEK]: timePeriods.yearToWeek,
    }
    return keyMap[dimension] || dimension
}

/**
 * Map time dimension to label translation key (long version for card labels)
 */
export const getTimeDimensionLabelKey = (dimension: string): string => {
    const { timePeriodLabels } = TRANSLATION_KEYS.myTargetPerformance
    const keyMap: Record<string, string> = {
        [TIME_DIMENSIONS.PRIOR_DAY]: timePeriodLabels.priorDay,
        [TIME_DIMENSIONS.PRIOR_WEEK]: timePeriodLabels.priorWeek,
        [TIME_DIMENSIONS.LAST_4_WEEKS]: timePeriodLabels.last4Weeks,
        [TIME_DIMENSIONS.PRIOR_MONTH]: timePeriodLabels.priorMonth,
        [TIME_DIMENSIONS.PRIOR_QUARTER]: timePeriodLabels.priorQuarter,
        [TIME_DIMENSIONS.PRIOR_YEAR]: timePeriodLabels.priorYear,
        [TIME_DIMENSIONS.WEEK_TO_DAY]: timePeriodLabels.weekToDay,
        [TIME_DIMENSIONS.MONTH_TO_DAY]: timePeriodLabels.monthToDay,
        [TIME_DIMENSIONS.MONTH_TO_WEEK]: timePeriodLabels.monthToWeek,
        [TIME_DIMENSIONS.QUARTER_TO_WEEK]: timePeriodLabels.quarterToWeek,
        [TIME_DIMENSIONS.YEAR_TO_DAY]: timePeriodLabels.yearToDay,
        [TIME_DIMENSIONS.YEAR_TO_WEEK]: timePeriodLabels.yearToWeek,
    }
    return keyMap[dimension] || dimension
}

type GFTimeDimension =
    | 'yesterday'
    | 'week_to_day'
    | 'last_week'
    | 'last_4_weeks'
    | 'month_to_day'
    | 'month_to_week'
    | 'prior_month'
    | 'quarter_to_week'
    | 'prior_quarter'
    | 'year_to_day'
    | 'year_to_week'
    | 'last_year'

const getGFTimeDimensionKeys = (gfDim: string) => {
    const { timePeriods, timePeriodLabels } =
        TRANSLATION_KEYS.myTargetPerformance
    const map: Record<GFTimeDimension, { short: string; label: string }> = {
        yesterday: {
            short: timePeriods.priorDay,
            label: timePeriodLabels.priorDay,
        },
        week_to_day: {
            short: timePeriods.weekToDay,
            label: timePeriodLabels.weekToDay,
        },
        last_week: {
            short: timePeriods.priorWeek,
            label: timePeriodLabels.priorWeek,
        },
        last_4_weeks: {
            short: timePeriods.last4Weeks,
            label: timePeriodLabels.last4Weeks,
        },
        month_to_day: {
            short: timePeriods.monthToDay,
            label: timePeriodLabels.monthToDay,
        },
        month_to_week: {
            short: timePeriods.monthToWeek,
            label: timePeriodLabels.monthToWeek,
        },
        prior_month: {
            short: timePeriods.priorMonth,
            label: timePeriodLabels.priorMonth,
        },
        quarter_to_week: {
            short: timePeriods.quarterToWeek,
            label: timePeriodLabels.quarterToWeek,
        },
        prior_quarter: {
            short: timePeriods.priorQuarter,
            label: timePeriodLabels.priorQuarter,
        },
        year_to_day: {
            short: timePeriods.yearToDay,
            label: timePeriodLabels.yearToDay,
        },
        year_to_week: {
            short: timePeriods.yearToWeek,
            label: timePeriodLabels.yearToWeek,
        },
        last_year: {
            short: timePeriods.priorYear,
            label: timePeriodLabels.priorYear,
        },
    }
    return map[gfDim as GFTimeDimension] ?? null
}

/**
 * Map GF format time dimension to label translation key (long version for headings)
 */
export const getGFTimeDimensionLabelKey = (gfDim: string): string =>
    getGFTimeDimensionKeys(gfDim)?.label ?? gfDim

/**
 * Map GF format time dimension to short translation key (e.g. "PW", "YTW")
 */
export const getGFTimeDimensionShortKey = (gfDim: string): string =>
    getGFTimeDimensionKeys(gfDim)?.short ?? gfDim

/**
 * Convert hex color code to className for metric tile headers
 */
export const getColorClassName = (hexColor: string | undefined): string => {
    if (!hexColor) return 'hc8-bg-disabled'

    switch (hexColor.toLowerCase()) {
        case '#007e33':
        case '#00a200':
        case '#00b050':
            return 'hc8-bg-success'
        case '#ffbb33':
        case '#ffa500':
        case '#ffc000':
        case '#ffff00':
            return 'metrics-header-attention'
        case '#cc0000':
        case '#ff0000':
            return 'hc8-bg-warning'
        default:
            return 'hc8-bg-disabled'
    }
}

/**
 * Find the column index for a metric in the GF card headings
 */
export const findMetricColumnIndex = (
    headings: Cell[] | undefined,
    metricName: string,
): number => {
    if (!headings) return -1
    const trimmedMetricName = metricName.trim()
    return headings.findIndex(
        (heading) =>
            heading.raw?.toString().trim() === trimmedMetricName ||
            heading.value?.toString().trim() === trimmedMetricName ||
            heading.raw?.toString().toLowerCase().trim() ===
                trimmedMetricName.toLowerCase(),
    )
}

/**
 * Get display name for a metric from GF card headings
 */
export const getMetricDisplayName = (
    cardData: TableData | undefined,
    metricName: string,
): string => {
    if (!cardData?.headings) return metricName

    const columnIndex = findMetricColumnIndex(cardData.headings, metricName)
    if (columnIndex === -1) return metricName

    return cardData.headings[columnIndex]?.value?.toString() || metricName
}

/**
 * Extract metric value from GF card data
 */
export const extractMetricValue = (
    cardData: TableData | undefined,
    metricName: string,
    rowIndex: number = 0,
): Cell | null => {
    if (!cardData?.headings || !cardData?.rows) return null

    const columnIndex = findMetricColumnIndex(cardData.headings, metricName)
    if (columnIndex === -1) return null

    const row = cardData.rows[rowIndex]
    if (!row?.[columnIndex]) return null

    return row[columnIndex]
}

export interface MetricForSorting {
    metric_name: string
    [key: string]: any
}

/**
 * Sort metrics array by their column index in the greenfield card
 */
export const sortMetricsByColumnIndex = <T extends MetricForSorting>(
    metrics: T[],
    greenfieldHeadings: Cell[] | undefined,
): T[] => {
    if (!greenfieldHeadings) return metrics

    return [...metrics].sort((a, b) => {
        const indexA = findMetricColumnIndex(greenfieldHeadings, a.metric_name)
        const indexB = findMetricColumnIndex(greenfieldHeadings, b.metric_name)

        // If both metrics are found in the greenfield card, sort by index
        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB
        }

        // If one metric is not found, push it to the end
        if (indexA === -1) return 1
        if (indexB === -1) return -1

        return 0
    })
}

/**
 * Sort section names by the minimum column index of their metrics
 */
export const sortSectionsByColumnIndex = <T extends MetricForSorting>(
    sectionNames: string[],
    metricsBySection: Record<string, T[]>,
    greenfieldHeadings: Cell[] | undefined,
): string[] => {
    if (!greenfieldHeadings) return sectionNames

    return [...sectionNames].sort((sectionA, sectionB) => {
        const metricsA = metricsBySection[sectionA]
        const metricsB = metricsBySection[sectionB]

        // Find minimum column index for each section
        const minIndexA = Math.min(
            ...metricsA
                .map((metric) =>
                    findMetricColumnIndex(
                        greenfieldHeadings,
                        metric.metric_name,
                    ),
                )
                .filter((idx) => idx !== -1),
        )
        const minIndexB = Math.min(
            ...metricsB
                .map((metric) =>
                    findMetricColumnIndex(
                        greenfieldHeadings,
                        metric.metric_name,
                    ),
                )
                .filter((idx) => idx !== -1),
        )

        // Handle cases where no valid indices are found
        if (!isFinite(minIndexA) && !isFinite(minIndexB)) return 0
        if (!isFinite(minIndexA)) return 1
        if (!isFinite(minIndexB)) return -1

        return minIndexA - minIndexB
    })
}

/**
 * Map GF time dimension format to green stores API timeframe code
 */
export const mapGFTimeToGreenStoresCode = (gfDim: string): string => {
    const mapping: Record<string, string> = {
        yesterday: 'PD',
        week_to_day: 'WTD',
        last_week: 'PW',
        last_4_weeks: 'L4W',
        month_to_day: 'MTD',
        month_to_week: 'MTW',
        prior_month: 'PM',
        quarter_to_week: 'QTW',
        prior_quarter: 'PQ',
        year_to_day: 'YTD',
        year_to_week: 'YTW',
        last_year: 'PY',
    }
    return mapping[gfDim] || gfDim
}

export type GreenStoresLookup = Map<string, Map<string, Cell>>

/**
 * Build a lookup map from green stores API data.
 * Keys: metric display name → timeframe code → Cell
 */
export const buildGreenStoresLookup = (
    data: TableData | undefined,
): GreenStoresLookup => {
    const lookup: GreenStoresLookup = new Map()
    if (!data?.rows || !data?.headings) return lookup

    const metricIdx = data.headings.findIndex((h) => h.raw === 'metric_name')
    const pctIdx = data.headings.findIndex((h) => h.raw === 'pct_green_stores')
    const timeIdx = data.headings.findIndex((h) => h.raw === 'timeframe')

    if (metricIdx === -1 || pctIdx === -1 || timeIdx === -1) {
        return lookup
    }

    for (const row of data.rows) {
        const name = row[metricIdx]?.raw?.toString() || ''
        const timeframe = row[timeIdx]?.raw?.toString() || ''
        if (!lookup.has(name)) lookup.set(name, new Map())
        lookup.get(name)!.set(timeframe, row[pctIdx])
    }

    return lookup
}

/**
 * Get green stores % for a metric and GF time dimension
 */
export const getGreenStoresPercent = (
    lookup: GreenStoresLookup,
    metricName: string,
    gfTimeDimension: string,
): Cell | null => {
    const code = mapGFTimeToGreenStoresCode(gfTimeDimension)
    return lookup.get(metricName)?.get(code) ?? null
}

export type DetailGreenStoresLookup = Map<string, string>

/**
 * Build a lookup for detail-level green stores data from all cards.
 * Cards WITH a location column are keyed by "metric|location|timeframe".
 * Cards WITHOUT a location column are keyed by "metric||timeframe" (aggregate).
 */
export const buildDetailGreenStoresLookup = (
    greenStoresData: Record<string, TableData | undefined>,
): DetailGreenStoresLookup => {
    const lookup: DetailGreenStoresLookup = new Map()

    for (const data of Object.values(greenStoresData)) {
        if (!data?.rows || !data?.headings) continue

        const matchCol = (h: Cell, name: string) =>
            h.raw === name || h.value === name
        const metricIdx = data.headings.findIndex(
            (h) => matchCol(h, 'metric_name') || matchCol(h, 'mpm_metric_nm'),
        )
        const pctIdx = data.headings.findIndex((h) =>
            matchCol(h, 'pct_green_stores'),
        )
        const timeIdx = data.headings.findIndex((h) => matchCol(h, 'timeframe'))
        if (metricIdx === -1 || pctIdx === -1 || timeIdx === -1) continue

        const locationIdx = findLocationColumnIndex(data.headings)

        for (const row of data.rows) {
            const metric = row[metricIdx]?.raw?.toString() || ''
            const location =
                locationIdx !== -1
                    ? row[locationIdx]?.value?.toString() || ''
                    : ''
            const timeframe = row[timeIdx]?.raw?.toString() || ''
            const pctValue = row[pctIdx]?.value?.toString() || ''

            const key = `${metric}|${location}|${timeframe}`
            lookup.set(key, pctValue)
        }
    }

    return lookup
}

/**
 * Find the location column index in GF card headings.
 * The location column has a raw value containing 'format_short'.
 */
export const findLocationColumnIndex = (
    headings: Cell[] | undefined,
): number => {
    if (!headings) return -1
    return headings.findIndex(
        (h) =>
            h.raw?.toString().includes('format_short') ||
            h.value?.toString().includes('format_short'),
    )
}

interface BuildDetailTableParams {
    metricName: string
    timeDimensions: readonly string[]
    dataByTimeDimension: Record<string, TableData | undefined>
    greenStoresData: Record<string, TableData | undefined>
    hasGreenStoresData: boolean
    currentLocation: string
    tabName: string
    t: TFunction
}

/**
 * Build the Canvas-compatible TableData for the metric detail view.
 * Extracted from MetricDetails to keep the component lean and enable unit testing.
 */
export const buildMetricDetailTableData = ({
    metricName,
    timeDimensions,
    dataByTimeDimension,
    greenStoresData,
    hasGreenStoresData,
    currentLocation,
    tabName,
    t,
}: BuildDetailTableParams): TableData | null => {
    const { myTargetPerformance } = TRANSLATION_KEYS

    const firstData = timeDimensions
        .map((d) => dataByTimeDimension[d])
        .find((d) => d?.rows?.length)
    if (!firstData || !metricName) return null

    const locationColIdx = findLocationColumnIndex(firstData.headings)

    const gsLookup = hasGreenStoresData
        ? buildDetailGreenStoresLookup(greenStoresData)
        : null

    // Per-dimension: only show % Green Stores if this metric has data for that timeframe
    const showGsPerDim: Record<string, boolean> = {}
    for (const dim of timeDimensions) {
        const gsCode = mapGFTimeToGreenStoresCode(dim)
        showGsPerDim[dim] =
            gsLookup !== null &&
            [...gsLookup.keys()].some(
                (key) =>
                    key.startsWith(`${metricName}|`) &&
                    key.endsWith(`|${gsCode}`),
            )
    }

    // Build headings
    const headings: Cell[] = [{ value: t(myTargetPerformance.location) }]
    for (const dim of timeDimensions) {
        headings.push({ value: t(getGFTimeDimensionShortKey(dim)) })
        if (showGsPerDim[dim]) {
            headings.push({
                id: `GreenStores_${dim}`,
                value: t(myTargetPerformance.pctGreenStores),
            })
        }
    }

    const encodedMetricName = encodeURIComponent(metricName)

    // Build location rows
    const rows: Cell[][] = (firstData.rows || []).map((row, rowIdx) => {
        const locName =
            locationColIdx !== -1
                ? row[locationColIdx]?.value?.toString() || ''
                : ''
        const locationUrl =
            locName && locName !== currentLocation && !isStore(locName)
                ? `/mytarget-performance/details/${encodedMetricName}?tab=${tabName}&locationId=${locName}`
                : undefined
        const cells: Cell[] = [
            { value: locName, ...(locationUrl ? { url: locationUrl } : {}) },
        ]
        for (const dim of timeDimensions) {
            const dimData = dataByTimeDimension[dim]
            const colIdx = findMetricColumnIndex(dimData?.headings, metricName)
            const cell =
                colIdx !== -1 ? dimData?.rows?.[rowIdx]?.[colIdx] : null
            cells.push({
                value: cell?.value?.toString() ?? '-',
                raw: cell?.raw?.toString() ?? cell?.value?.toString() ?? '-',
                className: adaptStatusColorToClass(cell?.color || ''),
            })
            if (showGsPerDim[dim]) {
                const gsCode = mapGFTimeToGreenStoresCode(dim)
                cells.push({
                    value:
                        gsLookup?.get(`${metricName}|${locName}|${gsCode}`) ??
                        '-',
                })
            }
        }
        return cells
    })

    // Build total row from footer
    const topRowLabel = isCompany(currentLocation)
        ? t(myTargetPerformance.company)
        : currentLocation
    const topRow: Cell[] = [{ value: topRowLabel }]
    for (const dim of timeDimensions) {
        const dimData = dataByTimeDimension[dim]
        const colIdx = findMetricColumnIndex(dimData?.headings, metricName)
        const cell = colIdx !== -1 ? dimData?.footer?.[colIdx] : null
        topRow.push({
            value: cell?.value?.toString() ?? '-',
            raw: cell?.raw?.toString() ?? cell?.value?.toString() ?? '-',
            className: adaptStatusColorToClass(cell?.color || ''),
        })
        if (showGsPerDim[dim]) {
            const gsCode = mapGFTimeToGreenStoresCode(dim)
            const gsKey = `${metricName}|${currentLocation}|${gsCode}`
            const gsKeyAgg = `${metricName}||${gsCode}`
            topRow.push({
                value: gsLookup?.get(gsKey) ?? gsLookup?.get(gsKeyAgg) ?? '-',
            })
        }
    }
    rows.unshift(topRow)

    return { headings, rows }
}

/**
 * Processes store-level table data by removing the aggregate row and sorting by store number.
 * Extracts the numeric portion from store IDs (e.g., "T0012" → 12) for proper numeric sorting.
 *
 * @param tableData - The raw table data containing store rows with aggregate row as first row
 * @returns TableData with aggregate row removed and stores sorted numerically, or null if input is null/undefined
 */
export const buildStoreTableDataWithSorting = (
    tableData: TableData | null | undefined,
): TableData | null => {
    if (!tableData) return null

    // Remove the first row (aggregate/total row) and sort by store number
    const rowsWithoutTotal = tableData.rows.slice(1)
    const sortedRows = rowsWithoutTotal.sort((a, b) => {
        const storeA = a[0]?.value?.toString() || ''
        const storeB = b[0]?.value?.toString() || ''

        // Extract numeric part after "T" prefix
        const numA = parseInt(storeA.replace(/^T/, ''), 10)
        const numB = parseInt(storeB.replace(/^T/, ''), 10)

        // Handle cases where parsing fails
        if (isNaN(numA) && isNaN(numB)) return 0
        if (isNaN(numA)) return 1
        if (isNaN(numB)) return -1

        return numA - numB
    })

    return {
        ...tableData,
        rows: sortedRows,
    }
}

/**
 * Build store-level table data for group locations.
 * Combines buildMetricDetailTableData and buildStoreTableDataWithSorting.
 * @returns TableData with stores sorted, or undefined if not a group location
 */
export const buildStoreTableData = ({
    isGroupLocation,
    metricName,
    timeDimensions,
    dataByTimeDimension,
    currentLocation,
    tabName,
    t,
}: {
    isGroupLocation: boolean
    metricName: string
    timeDimensions: readonly string[]
    dataByTimeDimension: Record<string, TableData | undefined>
    currentLocation: string
    tabName: string
    t: TFunction
}): TableData | null | undefined => {
    if (!isGroupLocation) return undefined

    const rawTableData = buildMetricDetailTableData({
        metricName,
        timeDimensions,
        dataByTimeDimension,
        greenStoresData: {},
        hasGreenStoresData: false,
        currentLocation,
        tabName,
        t,
    })

    return buildStoreTableDataWithSorting(rawTableData)
}
